/**
 * jornadaCierresHelpers.ts — Agrupa cierres por día + punto de venta (multicaja).
 */

import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { CierreCaja, Venta } from "../types/caja.types";
import {
  type JornadaTurnoKey,
  calendarDayFromIso,
  matchCierreToJornadaTurno,
  turnoSlotByKey,
} from "../constants/jornadaTurnos";
import {
  resolveCierreMetricasHistorial,
  resolveVentasForCierreDemo,
} from "../demo/resolveCierreDemoData";
import {
  sumVentasPorMetodo,
  totalMontos,
  type MontosPorMetodoPago,
} from "./propinaAllocation";

export interface JornadaCierreRow {
  cierre: CierreCaja;
  turnoLabel: string;
  turnoEmoji: string;
  orden: number;
}

export interface JornadaDiaConsolidado {
  baseInicial: number;
  totalVentas: number;
  totalEsperado: number;
  totalContado: number;
  descuadreNeto: number;
  cerradas: number;
  abiertas: number;
  movimientosTotal: number;
  ventasTotal: number;
}

/** Entrada del historial: un cierre suelto o jornada completa del día (todas las cajas). */
export type HistorialCierreEntry =
  | { kind: "single"; cierre: CierreCaja }
  | {
      kind: "dia";
      dayIso: string;
      cierres: CierreCaja[];
      consolidado: JornadaDiaConsolidado;
      cajasCount: number;
      turnosCount: number;
    };

export interface JornadaPuntoGrupo {
  puntoCajaId: string;
  puntoCajaNombre: string;
  cierres: CierreCaja[];
  consolidado: JornadaDiaConsolidado;
}

const DEFAULT_PUNTO_ID = "pc1";

export function resolveCierrePuntoId(cierre: CierreCaja): string {
  return cierre.puntoCajaId?.trim() || DEFAULT_PUNTO_ID;
}

export function resolveCierrePuntoNombre(
  cierre: CierreCaja,
  fallback = "Caja principal",
): string {
  return cierre.puntoCajaNombre?.trim() || fallback;
}

function historialDayKey(cierre: CierreCaja): string {
  return calendarDayFromIso(cierre.abiertaEn);
}

export function groupCierresByPunto(
  cierres: CierreCaja[],
): JornadaPuntoGrupo[] {
  const byPunto = new Map<string, CierreCaja[]>();
  for (const cierre of cierres) {
    const id = resolveCierrePuntoId(cierre);
    const list = byPunto.get(id) ?? [];
    list.push(cierre);
    byPunto.set(id, list);
  }

  return [...byPunto.entries()]
    .map(([puntoCajaId, list]) => {
      const sorted = [...list].sort(
        (a, b) =>
          new Date(a.abiertaEn).getTime() - new Date(b.abiertaEn).getTime(),
      );
      const first = sorted[0];
      return {
        puntoCajaId,
        puntoCajaNombre: resolveCierrePuntoNombre(first),
        cierres: sorted,
        consolidado: buildJornadaDiaConsolidado(sorted),
      };
    })
    .sort((a, b) => a.puntoCajaNombre.localeCompare(b.puntoCajaNombre));
}

export function countCajasUnicas(cierres: CierreCaja[]): number {
  return new Set(cierres.map(resolveCierrePuntoId)).size;
}

export function filterCierresByCalendarDay(
  cierres: CierreCaja[],
  dayIso: string,
): CierreCaja[] {
  return cierres.filter((c) => calendarDayFromIso(c.abiertaEn) === dayIso);
}

/** Lista cronológica de turnos (soporta más de 3 por caja y turnos repetidos). */
export function buildJornadaCierreRows(
  cierres: CierreCaja[],
): JornadaCierreRow[] {
  const sorted = [...cierres].sort(
    (a, b) => new Date(a.abiertaEn).getTime() - new Date(b.abiertaEn).getTime(),
  );
  const slotCounts = new Map<JornadaTurnoKey, number>();

  return sorted.map((cierre, index) => {
    const slotKey = matchCierreToJornadaTurno(cierre);
    let turnoLabel = cierre.label?.trim() || "";
    let turnoEmoji = "📋";

    if (slotKey) {
      const slot = turnoSlotByKey(slotKey);
      const count = (slotCounts.get(slotKey) ?? 0) + 1;
      slotCounts.set(slotKey, count);
      turnoEmoji = slot.emoji;
      if (!turnoLabel) turnoLabel = slot.label;
      if (count > 1) turnoLabel = `${turnoLabel} (${count})`;
    } else if (!turnoLabel) {
      turnoLabel = `Turno ${index + 1}`;
    }

    return {
      cierre,
      turnoLabel,
      turnoEmoji,
      orden: index + 1,
    };
  });
}

export function buildJornadaDiaConsolidado(
  cierresDelDia: CierreCaja[],
): JornadaDiaConsolidado {
  const acc = cierresDelDia.reduce(
    (state, cierre) => {
      const metrics = resolveCierreMetricasHistorial(cierre);
      state.totalEsperado += metrics.montoEsperado;
      state.totalContado += cierre.montoCierre ?? 0;
      state.descuadreNeto += metrics.diferencia;
      state.movimientosTotal += cierre.movimientosSnapshot?.length ?? 0;
      state.ventasTotal += resolveCierreVentasCountDisplay(cierre);
      if (cierre.estado === "cerrada") state.cerradas += 1;
      else state.abiertas += 1;
      return state;
    },
    {
      baseInicial: 0,
      totalVentas: 0,
      totalEsperado: 0,
      totalContado: 0,
      descuadreNeto: 0,
      cerradas: 0,
      abiertas: 0,
      movimientosTotal: 0,
      ventasTotal: 0,
    },
  );
  acc.baseInicial = jornadaBaseInicialPorCaja(cierresDelDia);
  acc.totalVentas = subtotalCobradoPorMetodo(
    ventasCobradasPorMetodoFromCierres(cierresDelDia),
  );
  return acc;
}

/**
 * Agrupa por día calendario: 1 fila por día si hay 2+ cierres (todas las cajas).
 */
export function groupCierresForHistorial(
  cierres: CierreCaja[],
): HistorialCierreEntry[] {
  const byDay = new Map<string, CierreCaja[]>();
  for (const cierre of cierres) {
    const key = historialDayKey(cierre);
    const list = byDay.get(key) ?? [];
    list.push(cierre);
    byDay.set(key, list);
  }

  const days = [...byDay.keys()].sort((a, b) => b.localeCompare(a));

  const entries: HistorialCierreEntry[] = [];
  for (const dayIso of days) {
    const list = (byDay.get(dayIso) ?? []).sort(
      (a, b) =>
        new Date(a.abiertaEn).getTime() - new Date(b.abiertaEn).getTime(),
    );
    if (list.length === 1) {
      entries.push({ kind: "single", cierre: list[0] });
      continue;
    }
    entries.push({
      kind: "dia",
      dayIso,
      cierres: list,
      consolidado: buildJornadaDiaConsolidado(list),
      cajasCount: countCajasUnicas(list),
      turnosCount: list.length,
    });
  }
  return entries;
}

export function historialEntryKey(entry: HistorialCierreEntry): string {
  if (entry.kind === "single") return entry.cierre.id;
  return `dia-${entry.dayIso}`;
}

export function formatJornadaDayLabel(dayIso: string): string {
  return format(new Date(`${dayIso}T12:00:00`), "d 'de' MMMM, yyyy", {
    locale: es,
  });
}

export function formatJornadaHorario(cierres: CierreCaja[]): {
  inicio: string;
  fin: string;
  operando: boolean;
} {
  const sorted = [...cierres].sort(
    (a, b) => new Date(a.abiertaEn).getTime() - new Date(b.abiertaEn).getTime(),
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const operando = sorted.some((c) => c.estado === "abierta");
  return {
    inicio: first?.abiertaEn
      ? format(new Date(first.abiertaEn), "h:mm a")
      : "--:--",
    fin: operando
      ? "Operando"
      : last?.cerradaEn
        ? format(new Date(last.cerradaEn), "h:mm a")
        : "--:--",
    operando,
  };
}

export function formatJornadaResponsablesLabel(cierres: CierreCaja[]): string {
  const names = [
    ...new Set(
      cierres.map((c) => c.closedByName || c.openedByName || c.userId),
    ),
  ];
  if (names.length === 1) return names[0];
  if (names.length === 2) return names.join(" · ");
  return `${names.length} responsables`;
}

export function formatJornadaTurnosLabel(cantidad: number): string {
  return cantidad === 1 ? "1 turno" : `${cantidad} turnos`;
}

export function formatJornadaCajasLabel(cantidad: number): string {
  return cantidad === 1 ? "1 caja" : `${cantidad} cajas`;
}

export function formatDiaOperativoLabel(
  cajasCount: number,
  turnosCount: number,
): string {
  return `${formatJornadaCajasLabel(cajasCount)} · ${formatJornadaTurnosLabel(turnosCount)}`;
}

/** Ventas activas de un cierre para badges del historial/jornada. */
export function resolveCierreVentasCountDisplay(cierre: CierreCaja): number {
  const activas =
    cierre.ventasSnapshot?.filter((v) => v.estado !== "anulada").length ?? 0;
  if (activas > 0) return activas;
  return cierre.ventasCount ?? 0;
}

export function formatCierreVentasLabel(count: number): string {
  return count === 1 ? "1 Venta" : `${count} Ventas`;
}

/** Subtítulo de un cierre en el historial: ventas activas o movimientos. */
export function cierreSubtotalLabel(
  cierre: CierreCaja,
  metrics: ReturnType<typeof resolveCierreMetricasHistorial>,
): string | null {
  const ventas = resolveCierreVentasCountDisplay(cierre);
  if (ventas > 0) return formatCierreVentasLabel(ventas);
  const movs = cierre.movimientosSnapshot?.length;
  if (movs !== undefined && movs > 0) return `${movs} Movimientos`;
  if (metrics.ventasCount !== undefined)
    return formatCierreVentasLabel(metrics.ventasCount);
  return null;
}

export function jornadaBaseInicialDia(cierres: CierreCaja[]): number {
  const sorted = [...cierres].sort(
    (a, b) => new Date(a.abiertaEn).getTime() - new Date(b.abiertaEn).getTime(),
  );
  return sorted[0]?.montoInicial ?? 0;
}

/** Suma la base inicial del primer turno de cada caja (no todos los turnos). */
export function jornadaBaseInicialPorCaja(cierres: CierreCaja[]): number {
  const firstByPunto = new Map<string, CierreCaja>();
  for (const cierre of cierres) {
    const id = resolveCierrePuntoId(cierre);
    const existing = firstByPunto.get(id);
    if (
      !existing ||
      new Date(cierre.abiertaEn).getTime() <
        new Date(existing.abiertaEn).getTime()
    ) {
      firstByPunto.set(id, cierre);
    }
  }
  return [...firstByPunto.values()].reduce(
    (sum, c) => sum + (c.montoInicial ?? 0),
    0,
  );
}

/** Ventas únicas de los snapshots de uno o más cierres (demo canónico incluido). */
export function collectVentasFromCierres(cierres: CierreCaja[]): Venta[] {
  const seen = new Set<string>();
  const out: Venta[] = [];
  for (const cierre of cierres) {
    const snaps = cierre.ventasSnapshot ?? [];
    const ventas = resolveVentasForCierreDemo(cierre, snaps);
    for (const v of ventas) {
      if (v.estado === "anulada" || seen.has(v.id)) continue;
      seen.add(v.id);
      out.push(v);
    }
  }
  return out;
}

/** Cobrado por método (incluye propina) — misma regla que ticket de cierre. */
export function ventasCobradasPorMetodoFromCierres(
  cierres: CierreCaja[],
): MontosPorMetodoPago {
  return sumVentasPorMetodo(collectVentasFromCierres(cierres));
}

export function subtotalCobradoPorMetodo(montos: MontosPorMetodoPago): number {
  return totalMontos(montos);
}
